/**
 * PinManager Component
 *
 * Manages all comment pins on a page using adaptive stacking pattern.
 *
 * Display modes:
 * - 1-3 threads on same element: Stacked pins with diagonal offset
 * - 4+ threads on same element: Grouped badge with picker
 *
 * Architecture:
 * - Groups threads by matched element
 * - Uses position: fixed with viewport-relative coordinates
 * - Re-renders on scroll/resize to keep pins attached to elements
 */

import React, { useCallback, useMemo, useSyncExternalStore } from 'react';
import { StackedPins } from './StackedPins';
import { GroupedBadge } from './GroupedBadge';
import type { Comment, MatchResult, TeamMember } from '../../lib/types';
import {
  groupThreadsByElement,
  getDisplayMode,
  calculatePinNumbers,
} from '../../lib/thread-grouping';

/**
 * Subscribe to scroll/resize events for pin positioning.
 */
function subscribeToPositionChanges(callback: () => void): () => void {
  window.addEventListener('scroll', callback, { passive: true });
  window.addEventListener('resize', callback, { passive: true });
  return () => {
    window.removeEventListener('scroll', callback);
    window.removeEventListener('resize', callback);
  };
}

// Snapshot is just a counter that increments on scroll/resize
let positionVersion = 0;
function getPositionSnapshot(): number {
  return positionVersion;
}

function incrementPositionVersion(): void {
  positionVersion++;
}

/**
 * Trigger continuous position updates for a duration (e.g., during sidebar animation).
 * Uses requestAnimationFrame to smoothly update pin positions as elements reflow.
 */
export function triggerPositionUpdateAnimation(durationMs: number = 200): void {
  const startTime = performance.now();

  function updateLoop() {
    incrementPositionVersion();
    // Dispatch a synthetic resize event to trigger useSyncExternalStore subscribers
    window.dispatchEvent(new Event('resize'));

    if (performance.now() - startTime < durationMs) {
      requestAnimationFrame(updateLoop);
    }
  }

  requestAnimationFrame(updateLoop);
}

function createPositionSubscription(callback: () => void): () => void {
  const handler = () => {
    incrementPositionVersion();
    callback();
  };
  return subscribeToPositionChanges(handler);
}

export interface PinManagerProps {
  /** All comments (including replies) */
  comments: Comment[];
  /** Map of comment ID to match result */
  matchResults: Map<string, MatchResult>;
  /** Set of unread comment IDs */
  unreadCommentIds?: Set<string>;
  /** Callback when a pin/thread is clicked */
  onPinClick: (comment: Comment, pinElement: HTMLElement) => void;
  /** Callback when hovering on a pin */
  onPinHover?: (commentId: string | null) => void;
  /** Currently expanded comment ID */
  expandedCommentId?: string;
  /** Map of author ID to team member (for grouped badge picker) */
  authors?: Map<string, TeamMember>;
  /** Whether sidebar is open (for collision detection in thread picker) */
  isSidebarOpen?: boolean;
}

interface PinPosition {
  top: number;
  left: number;
}

/**
 * Calculate pin position relative to an element.
 * Pin is positioned at top-right corner of the element.
 * Pin (32px) is centered on the corner with slight overlap.
 * Using 8px offset (~2xs spacing) and 20px left offset for visual balance.
 */
function calculatePinPosition(element: Element): PinPosition {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top - 8, // ~2xs spacing offset
    left: rect.right - 20, // 20px offset for visual balance
  };
}

export const PinManager: React.FC<PinManagerProps> = ({
  comments,
  matchResults,
  unreadCommentIds = new Set(),
  onPinClick,
  onPinHover,
  expandedCommentId,
  authors = new Map(),
  isSidebarOpen = false,
}) => {
  // Subscribe to scroll/resize to re-render when positions change
  useSyncExternalStore(createPositionSubscription, getPositionSnapshot);

  // Count replies for each parent
  const replyCounts = useMemo(() => {
    const counts = new Map<string, number>();
    comments.forEach(c => {
      if (c.threadId) {
        counts.set(c.threadId, (counts.get(c.threadId) || 0) + 1);
      }
    });
    return counts;
  }, [comments]);

  // Group threads by element
  const threadGroups = useMemo(
    () => groupThreadsByElement(comments, matchResults),
    [comments, matchResults]
  );

  // Calculate global pin numbers
  const pinNumbers = useMemo(
    () => calculatePinNumbers(threadGroups),
    [threadGroups]
  );

  // Handle hover
  const handlePinHover = useCallback(
    (commentId: string | null) => {
      onPinHover?.(commentId);
    },
    [onPinHover]
  );

  if (threadGroups.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="pin-manager"
      data-multidev-sdk="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 999997,
      }}
    >
      {threadGroups.map(group => {
        const position = calculatePinPosition(group.element);
        const displayMode = getDisplayMode(group);

        if (displayMode === 'stacked') {
          return (
            <StackedPins
              key={group.elementId}
              threads={group.threads}
              basePosition={position}
              unreadCommentIds={unreadCommentIds}
              onPinClick={onPinClick}
              onPinHover={handlePinHover}
              expandedCommentId={expandedCommentId}
              replyCounts={replyCounts}
              pinNumbers={pinNumbers}
              authors={authors}
              isSidebarOpen={isSidebarOpen}
            />
          );
        } else {
          return (
            <GroupedBadge
              key={group.elementId}
              elementId={group.elementId}
              threads={group.threads}
              position={position}
              onThreadSelect={onPinClick}
              authors={authors}
              replyCounts={replyCounts}
              unreadCommentIds={unreadCommentIds}
              isSidebarOpen={isSidebarOpen}
            />
          );
        }
      })}
    </div>
  );
};
